import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import * as tcmAction from '../../../app/modals-popovers/task-card-modal/task-card-modal.actions';
import * as jcmAction from '../../../app/molecules/job-card-modal/job-card-modal.actions';
import { Job } from '../../business-objects/job/job.model';
import { Task } from '../../business-objects/task/task.model';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import * as actions from './calendar.actions';
import { CalendarHelper } from './calendar.helper';
import * as selectors from './calendar.selectors';
import { DayModel } from './models/day.model';

@Injectable()
export class CalendarEffects {

    @Effect()
    init$ = this.actions$.ofType(actions.INIT)
        .switchMap(action => {

            const calendarDates = CalendarHelper.getDatesForMonth(
                action.payload.startDate
            );
            const from = calendarDates[0];
            const to = calendarDates[calendarDates.length - 1];

            this.appFacade.store.dispatch(new actions.SetLoadingAction({
                loading: true
            }));

            return forkJoin(
                this.getJobsByDate(action.payload.accountId, from, to),
                this.getTasksByDate(action.payload.accountId, from, to)
            )
                .map(([jobs, tasks]) => {
                    return calendarDates.map(date => ({
                        date,
                        jobs: jobs
                            .filter(job =>
                                date.isSame(job.jobDate, 'day')
                            )
                            .sort((a, b) =>
                                a.reference.localeCompare(b.reference)
                            ),
                        tasks: tasks.filter(
                            task => date.isSame(task.dueDate, 'day')
                        )
                    } as DayModel));
                })
                .map(results => new actions.InitSuccessAction({
                    dates: results,
                    startDate: action.payload.startDate
                }))
                .catch(error =>
                    of(new actions.InitFailAction({error}))
                )
                .do(() => {
                    this.appFacade.store.dispatch(new actions.SetLoadingAction({
                        loading: false
                    }));
                });
        });

    @Effect({dispatch: false})
    initError$ = this.actions$.ofType(actions.INIT_FAIL)
        .switchMap(action => {
            return this.appFacade.toast.toastDanger(action.payload.error);
        });

    @Effect()
    refresh$ = this.actions$.ofType(
        jcmAction.DESTROY,
        tcmAction.DESTROY
    )
        .switchMap(() => forkJoin(
            this.appFacade.store.select(selectors.getStartDate).take(1),
            this.appFacade.objects.account.store.getActive().take(1)
            ).map(([startDate, account]) => new actions.InitAction({
                startDate,
                accountId: account.id
            }))
        );

    constructor(
        protected actions$: Actions,
        protected appFacade: VasAppFacade
    ) {
    }

    getJobsByDate(
        accountId: string,
        from: moment.Moment,
        to: moment.Moment
    ): Observable<Array<Job>> {
        return this.appFacade.objects.job.api.search(
            accountId,
            {
                'filterGte[jobDate]': from.format('YYYY-MM-DD'),
                'filterLte[jobDate]': to.format('YYYY-MM-DD')
            } as any
        )
            .map(response => response.items)
            .catch((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    return of([]);
                }
                return _throw(error);
            });
    }

    getTasksByDate(
        accountId: string,
        from: moment.Moment,
        to: moment.Moment
    ): Observable<Array<Task>> {
        return this.appFacade.objects.task.api.getFiltered(
            accountId,
            {
                'filterGte[dueDate]': from.format('YYYY-MM-DD'),
                'filterLte[dueDate]': to.format('YYYY-MM-DD')
            } as any
        )
            .map(response => response.items)
            // @todo this is a work around VAS-200
            .map(items => items.filter(item => {
                const dueDate = moment(item.dueDate);
                return dueDate.isBetween(from, to);
            }))
            .catch((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    return of([]);
                }
                return _throw(error);
            });
    }
}



// WEBPACK FOOTER //
// ./src/vas/pages/job-calendar/calendar.effects.ts